import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { VERTICAL_META, VerticalIcon } from "@/lib/verticals";
import ProviderCard from "@/components/ProviderCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Star } from "@phosphor-icons/react";

const Search = () => {
  const [sp, setSp] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const vertical = sp.get("vertical") || "";
  const sort = sp.get("sort") || "rating";

  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    ["q", "vertical", "city", "max_price", "min_rating", "sort"].forEach((k) => {
      if (sp.get(k)) params[k] = sp.get(k);
    });
    try {
      const r = await api.get("/providers", { params });
      setProviders(r.data);
    } finally { setLoading(false); }
  }, [sp]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
      <div className="relative mb-4">
        <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setParam("q", q)}
          data-testid="search-input" placeholder="Cerca professionista…" className="pl-11 h-12 rounded-full" />
      </div>

      {/* Vertical chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        <button data-testid="chip-all" onClick={() => setParam("vertical", "")}
          className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${!vertical ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
          Tutti
        </button>
        {Object.entries(VERTICAL_META).map(([id, m]) => (
          <button key={id} data-testid={`chip-${id}`} onClick={() => setParam("vertical", id)}
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-colors ${vertical === id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            <VerticalIcon v={id} size={15} weight="bold" /> {m.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mt-4 mb-6">
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="w-44 rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rating">Miglior valutazione</SelectItem>
            <SelectItem value="price">Prezzo crescente</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sp.get("max_price") || "any"} onValueChange={(v) => setParam("max_price", v === "any" ? "" : v)}>
          <SelectTrigger className="w-40 rounded-full" data-testid="price-select"><SelectValue placeholder="Prezzo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Qualsiasi prezzo</SelectItem>
            <SelectItem value="30">Fino a €30</SelectItem>
            <SelectItem value="50">Fino a €50</SelectItem>
            <SelectItem value="100">Fino a €100</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={sp.get("min_rating") ? "default" : "outline"} className="rounded-full gap-1"
          data-testid="rating-filter" onClick={() => setParam("min_rating", sp.get("min_rating") ? "" : "4")}>
          <Star weight="fill" size={15} className="text-amber-400" /> 4+
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4" data-testid="results-count">
        {loading ? "Caricamento…" : `${providers.length} professionisti trovati`}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {providers.map((p, i) => <ProviderCard key={p.provider_id} p={p} index={i} />)}
      </div>
      {!loading && providers.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">Nessun risultato. Prova a cambiare i filtri.</div>
      )}
    </div>
  );
};

export default Search;
